import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { getMe } from "../services/meService";
import { getRoles } from "../services/rolesService";
import { hasPermission as hasPermFn, hasAny as hasAnyFn, ROLES, ROLE_LABELS } from "../utils/permissions";

const AuthContext = createContext(null);

const readLocalUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const readLocalPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const readLocalRoles = () => {
  try {
    const raw = localStorage.getItem("roles");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// Fallback system roles used when the backend list isn't loaded yet.
const SYSTEM_ROLES_FALLBACK = [
  { id: 0, role_key: ROLES.SUPER_ADMIN, label: ROLE_LABELS[ROLES.SUPER_ADMIN], is_system: 1 },
  { id: 0, role_key: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN], is_system: 1 },
  { id: 0, role_key: ROLES.SALES, label: ROLE_LABELS[ROLES.SALES], is_system: 1 },
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readLocalUser);
  const [permissions, setPermissions] = useState(readLocalPermissions);
  const [roles, setRoles] = useState(() => {
    const cached = readLocalRoles();
    return cached.length ? cached : SYSTEM_ROLES_FALLBACK;
  });
  const [loading, setLoading] = useState(false);

  const refreshRoles = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await getRoles();
      if (res?.status && Array.isArray(res.data)) {
        setRoles(res.data);
        localStorage.setItem("roles", JSON.stringify(res.data));
      }
    } catch {
      // keep cached list on failure
    }
  }, []);

  // Refresh the current session from the server (role + permissions).
  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setPermissions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getMe();
      if (res?.status) {
        setUser(res.user);
        setPermissions(res.permissions || []);
        localStorage.setItem("user", JSON.stringify(res.user));
        localStorage.setItem("permissions", JSON.stringify(res.permissions || []));
      }
    } catch {
      // leave cached session in place — interceptor handles 401
    } finally {
      setLoading(false);
    }
    refreshRoles();
  }, [refreshRoles]);

  useEffect(() => {
    // On mount, if we have a token but no cached user, try to fetch.
    const token = localStorage.getItem("token");
    if (token) {
      if (!user || !permissions?.length) {
        refresh();
      } else {
        refreshRoles();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPermission = useCallback(
    (key) => hasPermFn(user, permissions, key),
    [user, permissions]
  );
  const hasAny = useCallback(
    (keys) => hasAnyFn(user, permissions, keys),
    [user, permissions]
  );

  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const isAdmin = user?.role === ROLES.ADMIN;
  const isSales = user?.role === ROLES.SALES;

  // Resolve a role key to its display label, falling back to the key.
  const roleLabel = useCallback(
    (key) => {
      if (!key) return "";
      const found = roles.find((r) => r.role_key === key);
      if (found?.label) return found.label;
      return ROLE_LABELS[key] || key;
    },
    [roles]
  );

  const value = useMemo(
    () => ({
      user,
      permissions,
      roles,
      loading,
      refresh,
      refreshRoles,
      setUser,
      setPermissions,
      setRoles,
      hasPermission,
      hasAny,
      isSuperAdmin,
      isAdmin,
      isSales,
      roleLabel,
    }),
    [user, permissions, roles, loading, refresh, refreshRoles, hasPermission, hasAny, isSuperAdmin, isAdmin, isSales, roleLabel]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
};
